import React from 'react';
import { Navigate } from 'react-router-dom';
import Loading from '../Loading';
import NoPermissionFallback from '../roleGuard/elements/NoPermissionFallback';
import { useScopesForAba } from '../../../hooks/useScope';

/**
 * Scope-based sibling of <RoleGuard>. Same API shape (allowedRoles -> scope/
 * scopes, same behavior/redirectTo/fallback contract) so call sites can move
 * from role checks to scope checks one at a time without relearning a new
 * mental model. Does not replace RoleGuard — the two coexist; RoleGuard stays
 * for genuine role-identity checks, HasScope is for capability checks, which
 * is everything that used to be an "Owner-only" style access rule.
 *
 * @param {string} [scope] - Single scope key, e.g. 'feature:opensso:enabled'.
 * @param {string[]} [scopes] - Multiple scope keys; combine with `match`.
 * @param {'any' | 'all'} [match='any'] - How `scopes` are combined.
 * @param {string} [aba] - FI to check scopes for; defaults to the primary FI.
 * @param {'redirect' | 'fallback'} [behavior] - Required when scope(s) given.
 * @param {string} [redirectTo='/']
 * @param {React.ReactNode} [fallback]
 * @param {boolean} [isLoading] - Escape hatch for screens that want to defer
 *   to their own Page-level loading flag instead of rendering the deny state
 *   before the session has hydrated.
 * @param {React.ReactNode} children
 */
function HasScope({ scope, scopes, match = 'any', aba, behavior, redirectTo = '/', fallback, isLoading, children }) {
    const currentScopes = useScopesForAba(aba);

    const required = scope ? [scope] : (Array.isArray(scopes) ? scopes : null);

    if (isLoading) {
        return <Loading />;
    }

    if (!required) {
        return children; // no restriction requested — parity with allowedRoles={null}
    }

    const allowed = match === 'all'
        ? required.every((s) => currentScopes.includes(s))
        : required.some((s) => currentScopes.includes(s));

    if (allowed) {
        return children;
    }

    if (behavior === 'redirect') {
        // Declarative redirect rather than navigate() — routing from inside
        // render is a side effect React is free to run twice or discard.
        return <Navigate to={redirectTo} replace />;
    }

    return fallback !== undefined ? fallback : <NoPermissionFallback />;
}

export default HasScope;
